import { Icon } from '@chakra-ui/react'
import { ChevronDown, ChevronUp, Info } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAuthenticationState } from '~/atoms/AuthenticationState'
import { DropdownMangoButton } from '~/components/shared/Dropdown/DropdownMangoButton'
import { MenuPlacement } from '~/components/shared/Dropdown/Menu'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { MangoButtonVariant } from '~/components/shared/MangoButton'
import { Tooltip } from '~/components/ui/tooltip'
import { REGIONS } from '~/constants/regions'
import { Environment } from '~/types/EnvironmentTypes'
import { getApiEnvironment, setApiEnvironment } from '~/utils/common'
import { isElectron } from '~/utils/common'

const REGION_OPTIONS = [
  REGIONS[Environment.PRODUCTION],
  REGIONS[Environment.PRODUCTION_UK],
]

export const RegionSelector = () => {
  const { t } = useTranslation()
  const { clearAllAuthenticationData, isUserLoggedIn } =
    useAuthenticationState()
  const [isMenuVisible, setIsMenuVisible] = React.useState(false)

  if (!isElectron) return null

  // Only show region selector on login screen (when user is not logged in)
  if (isUserLoggedIn) return null

  const currentEnv = getApiEnvironment()

  // Always show US and UK production regions to end-users
  const currentRegion =
    currentEnv === Environment.PRODUCTION_UK
      ? Environment.PRODUCTION_UK
      : Environment.PRODUCTION

  const selectedOption = REGION_OPTIONS.find(
    (option) => option.environment === currentRegion,
  )

  const menuItems = REGION_OPTIONS.map((option) => {
    const { FlagIcon } = option
    return {
      content: (
        <MenuListItem
          primaryText={option.getLocalizedName()}
          leftContent={<FlagIcon width="16" height="12" />}
        />
      ),
      value: option.environment,
      onSelect: (value: Environment) => handleRegionChange(value),
    }
  }) as MenuItem<Environment>[]

  const handleRegionChange = (value: Environment) => {
    if (value !== currentRegion) {
      setApiEnvironment(value)
      clearAllAuthenticationData()
      window.location.reload()
    }
  }

  const handleMenuOpenChange = (isOpen: boolean) => {
    setIsMenuVisible(isOpen)
  }

  return (
    <>
      <DropdownMangoButton
        variant={MangoButtonVariant.Login}
        label={
          selectedOption
            ? `${selectedOption.abbreviation}`
            : t('regionSelector.selectRegion')
        }
        leftIcon={selectedOption ? selectedOption.FlagIcon : undefined}
        rightIcon={isMenuVisible ? ChevronUp : ChevronDown}
        items={menuItems}
        onMenuOpenChange={handleMenuOpenChange}
        mangoButtonOptions={{
          leftIconProps: {
            width: '16px',
            height: '12px',
            strokeWidth: 'revert-layer',
          },
          rightIconProps: {
            width: '.8rem',
            strokeWidth: 3,
          },
        }}
        menuOptions={{
          placement: MenuPlacement.BottomRight,
          width: '14rem',
        }}
      />
      <Tooltip content={t('regionSelector.regionInfoTooltip')}>
        <Icon boxSize="20px" css={{ color: 'brand.contrast' }}>
          <Info size="100%" />
        </Icon>
      </Tooltip>
    </>
  )
}
